/**
 * LCA Project View — phase tabs + PDF export.
 *
 * Embedded inside LCAModule. Receives projectId + onBack callback.
 * Phase components are loaded lazily; placeholders shown until they exist.
 *
 * Ported from standalone src/pages/ProjectView.jsx with platform adaptations:
 *   - lcaClient (no router)
 *   - PDF export uses fetch directly with /api/lca prefix
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Target, Package, BarChart3, Lightbulb, Database,
  Lock, ChevronRight, FileText, Download, Pencil, X, Check,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';

import lcaClient from '../../services/lcaClient';

import Phase1GoalScope from './Phase1GoalScope';
import Phase2Inventory from './Phase2Inventory';
import Phase3LCIA from './Phase3LCIA';
import Phase4Interpretation from './Phase4Interpretation';
import DatasetLibrary from './DatasetLibrary';

const phaseConfig = {
  1: { name: 'Goal & Scope', icon: Target, component: Phase1GoalScope },
  2: { name: 'Inventory (LCI)', icon: Package, component: Phase2Inventory },
  3: { name: 'Impact (LCIA)', icon: BarChart3, component: Phase3LCIA },
  4: { name: 'Interpretation', icon: Lightbulb, component: Phase4Interpretation },
};

// Datasets tab is special — it's not a numbered ISO phase, it's an always-
// available view that reuses the global DatasetLibrary scoped to the
// project's site so users only see factors relevant to this project.
const DATASETS_TAB = 'datasets';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LCAProjectView({ projectId, onBack }) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState(1);
  const [exporting, setExporting] = useState(false);
  // Product master inline picker — lets users attach / change / detach the
  // product on an existing project without leaving the workspace.
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');

  const fetchProject = useCallback(async () => {
    try {
      const response = await lcaClient.get(`/projects/${projectId}`);
      setProject(response.data);
      setActivePhase((cur) => cur || response.data.current_phase || 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load project');
      onBack?.();
    } finally {
      setLoading(false);
    }
  }, [projectId, onBack]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Load product master once we enter the workspace (cheap; tenant-wide list).
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${BACKEND_URL}/api/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setProducts(r.data?.products || []))
      .catch(() => {}); // silent — product picker just won't surface
  }, []);

  const currentProduct = project?.master_product_id
    ? products.find((p) => p.id === project.master_product_id)
    : null;

  const saveProduct = async (productIdOrNull) => {
    setSavingProduct(true);
    try {
      const { data } = await lcaClient.patch(`/projects/${projectId}`, {
        master_product_id: productIdOrNull,
      });
      setProject(data);
      setEditingProduct(false);
      toast.success(productIdOrNull ? 'Product linked' : 'Product unlinked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update product');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleExportPDF = async (format) => {
    setExporting(true);
    const formatLabels = {
      full: 'Full LCA Report',
      epd: 'EPD Format',
      cbam: 'CBAM Format',
    };
    try {
      toast.info(`Generating ${formatLabels[format]}...`);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/lca/projects/${projectId}/export/pdf/${format}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(project?.name || 'project').replace(/\s+/g, '_')}_${format.toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${formatLabels[format]} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${formatLabels[format]}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (!project) return null;

  const PhaseComponent = phaseConfig[activePhase]?.component;

  return (
    <div className="space-y-6" data-testid="lca-project-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-xl font-bold text-foreground">{project.name}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs">v{project.version}</Badge>
              {project.site_name && (
                <Badge variant="outline" className="text-xs">
                  {project.site_name}
                </Badge>
              )}

              {/* Product master link — view + inline edit */}
              {!editingProduct ? (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProductId(project.master_product_id || '');
                    setEditingProduct(true);
                  }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-teal-200 bg-teal-50 text-xs text-teal-800 hover:bg-teal-100"
                  title="Edit product linked to this project"
                >
                  <Package className="h-3 w-3" />
                  {currentProduct
                    ? <>
                        {currentProduct.name}
                        {currentProduct.code && <span className="text-teal-500">({currentProduct.code})</span>}
                      </>
                    : <span className="text-teal-700">+ Link product</span>}
                  <Pencil className="h-3 w-3 ml-1 text-teal-500" />
                </button>
              ) : (
                <div className="inline-flex items-center gap-1">
                  <select
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    disabled={savingProduct}
                    className="text-xs border border-gray-300 rounded px-2 py-0.5"
                  >
                    <option value="">— Unlink —</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.code ? ` (${p.code})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => saveProduct(selectedProductId || null)}
                    disabled={savingProduct}
                    className="p-1 rounded text-green-700 hover:bg-green-50"
                    title="Save"
                  >
                    <Check className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingProduct(false)}
                    disabled={savingProduct}
                    className="p-1 rounded text-gray-600 hover:bg-gray-100"
                    title="Cancel"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}

              <span className="text-xs text-muted-foreground">
                Phase {project.current_phase} Active
              </span>
            </div>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={exporting}>
              {exporting ? (
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export PDF
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>PDF Export Options</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportPDF('full')}>
              <FileText className="h-4 w-4 mr-2" />
              <div>
                <div className="font-medium">Full LCA Report</div>
                <div className="text-xs text-muted-foreground">Complete report</div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportPDF('epd')}>
              <FileText className="h-4 w-4 mr-2" />
              <div>
                <div className="font-medium">EPD Format</div>
                <div className="text-xs text-muted-foreground">ISO 14025 compliant</div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportPDF('cbam')}>
              <FileText className="h-4 w-4 mr-2" />
              <div>
                <div className="font-medium">CBAM Format</div>
                <div className="text-xs text-muted-foreground">EU CBAM</div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-border">
        {[1, 2, 3, 4].map((phase) => {
          const config = phaseConfig[phase];
          const PhaseIcon = config.icon;
          const status = (project.phase_statuses || {})[String(phase)];
          const isLocked = status === 'locked';
          const isActive = activePhase === phase;
          const isCurrent = project.current_phase === phase;
          const canAccess = phase <= project.current_phase || isLocked;

          return (
            <React.Fragment key={phase}>
              <button
                onClick={() => canAccess && setActivePhase(phase)}
                disabled={!canAccess}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : isLocked
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : isCurrent
                    ? 'bg-secondary text-foreground'
                    : 'bg-muted text-muted-foreground'
                } ${!canAccess ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {isLocked ? <Lock className="h-4 w-4" /> : <PhaseIcon className="h-4 w-4" />}
                <span className="font-medium text-sm whitespace-nowrap">{config.name}</span>
              </button>
              <ChevronRight className="h-4 w-4 text-muted-foreground/30 flex-shrink-0" />
            </React.Fragment>
          );
        })}
        {/* Datasets tab — always accessible, scoped to project's site */}
        <button
          onClick={() => setActivePhase(DATASETS_TAB)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all cursor-pointer ${
            activePhase === DATASETS_TAB
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-secondary'
          }`}
        >
          <Database className="h-4 w-4" />
          <span className="font-medium text-sm whitespace-nowrap">Datasets</span>
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activePhase}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activePhase === DATASETS_TAB ? (
            <DatasetLibrary
              study={project ? { site_id: project.site_id, site_name: project.site_name } : null}
            />
          ) : PhaseComponent ? (
            <PhaseComponent
              project={project}
              onUpdate={fetchProject}
              isLocked={(project.phase_statuses || {})[String(activePhase)] === 'locked'}
            />
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
